import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type {
  NotificationListItem,
  NotificationSettings,
  NotificationsResponse,
  UnreadCountResponse,
} from './contracts.js';

/**
 * The in-app bell data layer (NOTIF-03 / D-13). The ~30s short-poll is the BASELINE: TanStack's
 * `refetchInterval` keeps the bell fresh without a socket. Plan 05's WebSocket layer mounts on TOP
 * of this — it pushes live arrivals and reduces reliance on the poll, but never replaces it (D-15
 * fallback). Every request goes through `apiFetch` (the only fetch path — `credentials:'include'` +
 * `X-Tenant-Subdomain` + single-flight 401 refresh baked in). The tenant is derived server-side
 * from the auth context; the SPA never sends a tenant id.
 *
 * Mark-read is OPTIMISTIC (the badge/dots flip immediately), reconciled by the next poll/RPC; on
 * failure the cache is rolled back. Opening the panel does NOT auto-mark-read (explicit only).
 */

const POLL_INTERVAL_MS = 30_000;

export const notificationsQueryKey = ['notifications', 'list'] as const;
export const unreadCountQueryKey = ['notifications', 'unread-count'] as const;
export const notificationSettingsQueryKey = ['notifications', 'settings'] as const;

/** The bell list on the ~30s short-poll baseline (NOTIF-03). */
export function useNotificationsQuery() {
  return useQuery<NotificationsResponse, Error, NotificationListItem[]>({
    queryKey: notificationsQueryKey,
    queryFn: () => apiFetchJson<NotificationsResponse>('/api/v1/notifications'),
    refetchInterval: POLL_INTERVAL_MS,
    select: (data) => data.notifications ?? [],
  });
}

/** The unread-count badge (server truth), on the same short-poll cadence. */
export function useUnreadCountQuery() {
  return useQuery<UnreadCountResponse, Error, number>({
    queryKey: unreadCountQueryKey,
    queryFn: () => apiFetchJson<UnreadCountResponse>('/api/v1/notifications/unread-count'),
    refetchInterval: POLL_INTERVAL_MS,
    select: (data) => data.unread ?? 0,
  });
}

/**
 * Mark one or many deliveries read in ONE batched call (NOTIF-07). Optimistic: the list + unread
 * count update immediately, reconciled by the next poll; on error the previous cache is restored.
 */
export function useMarkReadMutation() {
  const qc = useQueryClient();

  return useMutation<
    { ok: boolean },
    Error,
    number[],
    { prevList?: NotificationListItem[]; prevUnread?: number }
  >({
    mutationFn: (deliveryIds: number[]) =>
      apiFetchJson<{ ok: boolean }>('/api/v1/notifications/mark-read', {
        method: 'POST',
        body: JSON.stringify({ deliveryIds }),
      }),
    onMutate: async (deliveryIds) => {
      await qc.cancelQueries({ queryKey: notificationsQueryKey });
      await qc.cancelQueries({ queryKey: unreadCountQueryKey });

      const prevListResponse = qc.getQueryData<NotificationsResponse>(notificationsQueryKey);
      const prevUnreadResponse = qc.getQueryData<UnreadCountResponse>(unreadCountQueryKey);
      const ids = new Set(deliveryIds);

      // Optimistically flip the targeted deliveries to 'read'.
      if (prevListResponse?.notifications) {
        qc.setQueryData<NotificationsResponse>(notificationsQueryKey, {
          ...prevListResponse,
          notifications: prevListResponse.notifications.map((n) =>
            ids.has(n.id) ? { ...n, state: 'read', readAt: new Date().toISOString() } : n,
          ),
        });
      }
      // Optimistically drop the unread count by the number of unread targets.
      if (prevUnreadResponse) {
        const newlyRead = (prevListResponse?.notifications ?? []).filter(
          (n) => ids.has(n.id) && n.state !== 'read',
        ).length;
        qc.setQueryData<UnreadCountResponse>(unreadCountQueryKey, {
          ...prevUnreadResponse,
          unread: Math.max(0, (prevUnreadResponse.unread ?? 0) - newlyRead),
        });
      }

      return {
        prevList: prevListResponse?.notifications,
        prevUnread: prevUnreadResponse?.unread,
      };
    },
    onError: (_err, _ids, context) => {
      // Roll back to the pre-optimistic cache on failure.
      if (context?.prevList) {
        qc.setQueryData<NotificationsResponse>(notificationsQueryKey, (old) =>
          old ? { ...old, notifications: context.prevList } : old,
        );
      }
      if (context?.prevUnread !== undefined) {
        qc.setQueryData<UnreadCountResponse>(unreadCountQueryKey, (old) =>
          old ? { ...old, unread: context.prevUnread } : old,
        );
      }
    },
    onSettled: () => {
      // Reconcile with server truth.
      void qc.invalidateQueries({ queryKey: notificationsQueryKey });
      void qc.invalidateQueries({ queryKey: unreadCountQueryKey });
    },
  });
}

/** Read the user's notification settings (master push + per-category). */
export function useNotificationSettingsQuery() {
  return useQuery<{ settings?: NotificationSettings }, Error, NotificationSettings>({
    queryKey: notificationSettingsQueryKey,
    queryFn: () =>
      apiFetchJson<{ settings?: NotificationSettings }>('/api/v1/notifications/settings'),
    select: (data) =>
      data.settings ?? { masterPush: true, announcements: true, jobs: true, invoices: true },
  });
}

/**
 * Persist the user's settings (optimistic + "Saved" confirm). The always-on categories are never
 * sent — the server also rejects them (D-13s); the UI locks them.
 */
export function useUpdateSettingsMutation() {
  const qc = useQueryClient();

  return useMutation<
    { settings?: NotificationSettings },
    Error,
    NotificationSettings,
    { prev?: NotificationSettings }
  >({
    mutationFn: (settings: NotificationSettings) =>
      apiFetchJson<{ settings?: NotificationSettings }>('/api/v1/notifications/settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
      }),
    onMutate: async (settings) => {
      await qc.cancelQueries({ queryKey: notificationSettingsQueryKey });
      const prev = qc.getQueryData<{ settings?: NotificationSettings }>(
        notificationSettingsQueryKey,
      );
      qc.setQueryData<{ settings?: NotificationSettings }>(notificationSettingsQueryKey, {
        settings,
      });
      return { prev: prev?.settings };
    },
    onError: (_err, _settings, context) => {
      if (context?.prev) {
        qc.setQueryData<{ settings?: NotificationSettings }>(notificationSettingsQueryKey, {
          settings: context.prev,
        });
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationSettingsQueryKey });
    },
  });
}
